import React from 'react';
import {
    TopologyNode,
    AlgoMap,
    resolveAlgoLocatorName,
} from '../TopologyData';

interface Props {
    node: TopologyNode;
    onClose: () => void;
    algoMap?: AlgoMap;
}

const NodeDetailPanel: React.FC<Props> = ({ node, onClose, algoMap }) => {
    const isRR = node.type === 'route-reflector';

    return (
        <div
            className="w-80 h-full overflow-y-auto bg-semantic-bg-primary
                border-l border-semantic-border-primary p-4
                flex flex-col gap-3"
            style={{ fontFamily: '"Quicksand", sans-serif' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3
                    className="text-sm font-bold
                        text-semantic-text-primary"
                >
                    Node Details
                </h3>
                <button
                    onClick={onClose}
                    className="text-semantic-text-secondary
                        hover:text-semantic-text-primary text-lg"
                >
                    &times;
                </button>
            </div>

            {/* Identity */}
            <Section title="Identity">
                <Row label="Hostname" value={node.hostname} />
                <Row label="LSP ID" value={node.lspId} />
                <Row label="Type" value={isRR ? 'Route Reflector' : 'Router'} />
                <Row label="Role" value={node.role} />
                {node.isLocal && (
                    <span
                        className="inline-block text-[10px] font-bold
                            bg-semantic-bg-danger-bold text-semantic-text-inverse-primary
                            px-2 py-0.5 rounded mt-1"
                    >
                        LOCAL
                    </span>
                )}
            </Section>

            {/* Location */}
            <Section title="Location">
                <Row
                    label="Datacenter"
                    value={node.location.datacenterId || node.site}
                />
                <Row label="City" value={node.location.city} />
                <Row label="Country" value={node.location.country} />
                <Row label="Label" value={node.location.label} />
            </Section>

            {/* SR / Flex-Algo */}
            <Section title="Segment Routing">
                <Row label="Area" value={node.areaAddress} />
                <Row
                    label="SR Algorithms"
                    value={`${node.srAlgorithms.length} algorithms`}
                />
                <Row
                    label="Flex-Algo"
                    value={`${node.flexAlgoCount} configured`}
                />
            </Section>

            {/* SRv6 Locators */}
            <Section title={`SRv6 Locators (${node.srv6Locators.length})`}>
                <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-[10px]">
                        <thead>
                            <tr className="text-semantic-text-secondary">
                                <th className="text-left py-0.5 pr-1">
                                    Prefix
                                </th>
                                <th className="text-left py-0.5 px-1">
                                    Name
                                </th>
                                <th className="text-right py-0.5 pl-1">
                                    Algo
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {node.srv6Locators.map((loc, i) => {
                                const name = resolveAlgoLocatorName(
                                    loc.algorithm,
                                    algoMap,
                                    loc.locatorName
                                );
                                const hasFriendlyName =
                                    !name.startsWith('Algo-');

                                return (
                                <tr
                                    key={i}
                                    className="border-t
                                        border-semantic-border-secondary"
                                >
                                    <td
                                        className="py-0.5 pr-1 font-mono
                                            text-semantic-text-primary"
                                    >
                                        {loc.prefix}
                                    </td>
                                    <td
                                        className="py-0.5 px-1
                                            text-semantic-text-primary
                                            font-semibold"
                                    >
                                        {hasFriendlyName ? name : '—'}
                                    </td>
                                    <td
                                        className="py-0.5 pl-1 text-right
                                            text-semantic-text-secondary"
                                    >
                                        {loc.algorithm}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Prefixes */}
            <Section title={`Prefixes (${node.prefixes.length})`}>
                {node.prefixes.map((p, i) => (
                    <div
                        key={i}
                        className="text-[10px] font-mono
                            text-semantic-text-primary"
                    >
                        {p.prefix}{' '}
                        <span className="text-semantic-text-secondary">
                            metric:{p.metric}
                        </span>
                    </div>
                ))}
            </Section>
        </div>
    );
};

const Section: React.FC<{
    title: string;
    children: React.ReactNode;
}> = ({ title, children }) => (
    <div>
        <h4
            className="text-[11px] font-semibold
                text-semantic-text-secondary uppercase tracking-wide
                mb-1"
        >
            {title}
        </h4>
        <div className="flex flex-col gap-0.5">{children}</div>
    </div>
);

const Row: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex justify-between text-[11px]">
        <span className="text-semantic-text-secondary">{label}</span>
        <span className="text-semantic-text-primary font-medium">
            {value}
        </span>
    </div>
);

export default NodeDetailPanel;
